/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {DLSTheme} from '../../types/types';
import {PrimaryButton} from '../buttons/PrimaryButton';
import {ButtonSpinner} from '../loading/ButtonSpinner';

export interface MobileActionContentProps {
  children: string | JSX.Element;
  onMenuClose?: () => void;
  onSubmit: () => void;
  onCancel?: () => void;
  loading?: boolean;
  primaryButtonDisabled?: boolean;
  secondaryButtonDisabled?: boolean;
  hideActionButtons?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  content : {
    display        : 'flex',
    flexDirection  : 'column',
    justifyContent : 'space-between'
  },
  actionButtons : {
    display    : 'flex',
    alignItems : 'center',
    width      : 'calc(100vw - 48px)',
    marginTop  : '15px'
  },
  applyButton : {
    width                          : '286px',
    marginRight                    : '15px',
    [theme.breakpoints.down('sm')] : {
      width       : '100%',
      marginRight : '0px'
    }
  }
}));

/**
 * @MobileActionContent
 *
 * Component for mobile that needs some sort of cta. The DLS designs
 * typically repeat this pattern of showing a bottom drawer with a
 * done button pinned to the bottom with content in there.
 *
 * @param {JSX.Element} children: children passed to the component
 * @param {Function} onSubmit: callback gets fired when user clicks done CTA
 * @param {boolean} loading: tells the done button if should be disabled and show spinner
 * @param {boolean} primaryButtonDisabled: if primary cta should be disabled
 *
 * ```typescript
 *
 * import {MobileActionContent} from '@ion/dls/src/components/menus';
 *
 * <MobileActionContent loading={loading}
 *   onSubmit={handleSubmit}
 *   primaryButtonDisabled={isDisabledForm}>
 *
 * ```
 *
 */
export const MobileActionContent = ({
  children,
  onSubmit,
  primaryButtonDisabled,
  loading = false,
  hideActionButtons = false
}: MobileActionContentProps) => {
  const classes   = useStyles();

  return (
    <div className={classes.content}>
      <div>
        {children}
      </div>
      {!hideActionButtons && (
        <div className={classes.actionButtons}>
          <div className={classes.applyButton}>
            <PrimaryButton fullWidth
              disabled={loading || primaryButtonDisabled}
              onClick={onSubmit}>
              {
                loading ? (
                  <ButtonSpinner />
                ) : <>{translate('common:editors.shared.done')}</>
              }
            </PrimaryButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileActionContent;
